/**
 * Local fixtures for tests. Production reads use the live APIs
 * (`implementationRecordConfig.useMock` / `useListMock` stay false).
 */

import type {
  AssessmentPack,
  ImplementationPaginatedResult,
  ImplementationRecord,
  ImplementationRecordGroup,
  ImplementationRecordListQuery,
} from "./implementationRecordService";

const SUMMARY_MD =
  "### Summary\nOverall Assessment: **Partially Compliant**. Gaps were found across SOPs compared to GMP requirements.";
const ANALYSIS_MD =
  "## Analysis\n- Complaint response SLA exceeds the 30-day requirement.\n- QA review handoff is not documented.\n- Training records for impacted roles are incomplete.";
const RECOMMENDATION_MD =
  "## Recommended Actions\n- Update the Deviation Management SOP\n- Clarify QA review ownership\n- Retrain impacted user groups";

export const MOCK_IMPLEMENTATION_RECORDS: ImplementationRecord[] = [
  {
    id: "IMP-000001",
    planId: "TAG_12",
    assessmentSource: "classification",
    assessmentName: "Drug Impact - Safety",
    assessor: "system@freyrsolutions.com",
    assessorName: "System",
    referenceObject: {
      objectType: "insight",
      objectSubType: "regulation",
      objectTypeName: "Insight",
      objectSubTypeName: "Regulation",
      id: "R-122345",
      name: "Insight DocType = Regulation",
      version: "2.0",
    },
    impactedObject: {
      objectType: "concept.Department",
      objectSubType: "",
      objectTypeName: "Department",
      objectSubTypeName: "",
      id: "Safety",
      name: "Department = Safety",
      version: null,
    },
    impacted: true,
    impactedLevel: "high",
    assessmentSummary: SUMMARY_MD,
    detailedAnalysis: ANALYSIS_MD,
    recommendation: RECOMMENDATION_MD,
    assessmentRecordItems: [],
    assessedAt: "2026-09-20T11:02:00Z",
  },
  {
    id: "IMP-000002",
    planId: "TAG_12",
    assessmentSource: "classification",
    assessmentName: "Drug Impact - Safety",
    assessor: "system@freyrsolutions.com",
    assessorName: "System",
    referenceObject: {
      objectType: "insight",
      objectSubType: "regulation",
      objectTypeName: "Insight",
      objectSubTypeName: "Regulation",
      id: "R-122345",
      name: "Insight DocType = Regulation",
      version: "2.0",
    },
    impactedObject: {
      objectType: "concept.Topic",
      objectSubType: "",
      objectTypeName: "Topic",
      objectSubTypeName: "",
      id: "Local_PV_Process",
      name: "Topic = Local; PV Process",
      version: null,
    },
    impacted: true,
    impactedLevel: "medium",
    assessmentSummary: SUMMARY_MD,
    detailedAnalysis: ANALYSIS_MD,
    recommendation: RECOMMENDATION_MD,
    assessmentRecordItems: [],
    assessedAt: "2026-09-20T11:05:00Z",
  },
  {
    id: "IMP-000003",
    planId: "TAG_15",
    assessmentSource: "classification",
    assessmentName: "Regulations relate to SSI and CSI",
    assessor: "system@freyrsolutions.com",
    assessorName: "System",
    referenceObject: {
      objectType: "insight",
      objectSubType: "regulation",
      objectTypeName: "Insight",
      objectSubTypeName: "Regulation",
      id: "R-122345",
      name: "Insight DocType = Regulation",
      version: "1.0",
    },
    impactedObject: {
      objectType: "concept.Topic",
      objectSubType: "",
      objectTypeName: "Topic",
      objectSubTypeName: "",
      id: "Clinical_PV_System",
      name: "Topic = Clinical PV System",
      version: null,
    },
    impacted: false,
    impactedLevel: null,
    assessmentSummary: "Overall Assessment: **Not Impacted**.",
    detailedAnalysis: null,
    recommendation: null,
    assessmentRecordItems: [],
    assessedAt: "2026-09-19T09:00:00Z",
  },
  {
    id: "IMP-000004",
    planId: "OTO_3",
    assessmentSource: "assessment",
    assessmentName: "GSK Safety Impact Assessment",
    assessor: "system@freyrsolutions.com",
    assessorName: "System",
    referenceObject: {
      objectType: "insight",
      objectSubType: "regulation",
      objectTypeName: "Insight",
      objectSubTypeName: "Regulation",
      id: "INS163650",
      name: "EU MDR 2017/745",
      version: "2.0",
    },
    impactedObject: {
      objectType: "product",
      objectSubType: "device",
      objectTypeName: "Product",
      objectSubTypeName: "Device",
      id: "P00123",
      name: "Product = All Products",
      version: null,
    },
    impacted: true,
    impactedLevel: "high",
    assessmentSummary: SUMMARY_MD,
    detailedAnalysis: ANALYSIS_MD,
    recommendation: RECOMMENDATION_MD,
    assessmentRecordItems: [],
    assessedAt: "2026-09-18T14:20:00Z",
  },
  {
    id: "IMP-000005",
    planId: "OTO_3",
    assessmentSource: "assessment",
    assessmentName: "GSK Safety Impact Assessment",
    assessor: "lily.yang@freyrsolutions.com",
    assessorName: "Lily Yang",
    referenceObject: {
      objectType: "insight",
      objectSubType: "regulation",
      objectTypeName: "Insight",
      objectSubTypeName: "Regulation",
      id: "INS163650",
      name: "EU MDR 2017/745",
      version: "2.0",
    },
    impactedObject: {
      objectType: "docs",
      objectSubType: "sop",
      objectTypeName: "Document",
      objectSubTypeName: "SOP",
      id: "SOP0015678",
      name: "Complaint Handling SOP",
      version: null,
    },
    impacted: true,
    impactedLevel: "low",
    assessmentSummary: SUMMARY_MD,
    detailedAnalysis: ANALYSIS_MD,
    recommendation: RECOMMENDATION_MD,
    assessmentRecordItems: [],
    assessedAt: "2026-09-18T15:00:00Z",
  },
  {
    id: "IMP-000006",
    planId: "OTO_5",
    assessmentSource: "assessment",
    assessmentName: "SOP impact assessment",
    assessor: "system@freyrsolutions.com",
    assessorName: "System",
    referenceObject: {
      objectType: "insight",
      objectSubType: "regulation",
      objectTypeName: "Insight",
      objectSubTypeName: "Regulation",
      id: "INS163650",
      name: "EU MDR 2017/745",
      version: "2.0",
    },
    impactedObject: {
      objectType: "docs",
      objectSubType: "sop",
      objectTypeName: "Document",
      objectSubTypeName: "SOP",
      id: "DOC456",
      name: "Deviation Management SOP",
      version: null,
    },
    impacted: true,
    impactedLevel: "medium",
    assessmentSummary: SUMMARY_MD,
    detailedAnalysis: ANALYSIS_MD,
    recommendation: RECOMMENDATION_MD,
    assessmentRecordItems: [],
    assessedAt: "2026-09-17T10:00:00Z",
  },
  {
    id: "IMP-000007",
    planId: "OTO_5",
    assessmentSource: "assessment",
    assessmentName: "SOP impact assessment",
    assessor: "mick.liu@freyrsolutions.com",
    assessorName: "Mick Liu",
    referenceObject: {
      objectType: "insight",
      objectSubType: "regulation",
      objectTypeName: "Insight",
      objectSubTypeName: "Regulation",
      id: "INS163650",
      name: "EU MDR 2017/745",
      version: "2.0",
    },
    impactedObject: {
      objectType: "docs",
      objectSubType: "sop",
      objectTypeName: "Document",
      objectSubTypeName: "SOP",
      id: "DOC457",
      name: "Change Control SOP",
      version: "1.0",
    },
    impacted: false,
    impactedLevel: "no",
    assessmentSummary: "Overall Assessment: **Not Impacted**.",
    detailedAnalysis: null,
    recommendation: null,
    assessmentRecordItems: [],
    assessedAt: "2026-09-16T08:30:00Z",
  },
  {
    id: "IMP-000008",
    planId: "TAG_20",
    assessmentSource: "classification",
    assessmentName: "Market Impact - EU",
    assessor: "system@freyrsolutions.com",
    assessorName: "System",
    referenceObject: {
      objectType: "insight",
      objectSubType: "guidance",
      objectTypeName: "Insight",
      objectSubTypeName: "Guidance",
      id: "G-90001",
      name: "Insight DocType = Guidance",
      version: null,
    },
    impactedObject: {
      objectType: "concept.Country",
      objectSubType: "",
      objectTypeName: "Country",
      objectSubTypeName: "",
      id: "AT",
      name: "Country = Austria",
      version: null,
    },
    impacted: true,
    impactedLevel: "low",
    assessmentSummary: SUMMARY_MD,
    detailedAnalysis: ANALYSIS_MD,
    recommendation: RECOMMENDATION_MD,
    assessmentRecordItems: [],
    assessedAt: "2026-09-15T12:00:00Z",
  },
];

function asList(value?: string | readonly string[]): string[] {
  if (value == null) return [];
  const list = Array.isArray(value) ? value : [value];
  return list.map((item) => item.trim()).filter(Boolean);
}

function asBooleanList(value?: boolean | readonly boolean[]): boolean[] {
  if (value == null) return [];
  return Array.isArray(value) ? [...value] : [value];
}

function matchesQuery(
  record: ImplementationRecord,
  query: ImplementationRecordListQuery,
): boolean {
  const planIds = asList(query.planId);
  if (planIds.length && !planIds.includes(record.planId)) return false;
  const sources = asList(query.assessmentSource);
  if (sources.length && !sources.includes(record.assessmentSource)) {
    return false;
  }
  const names = asList(query.assessmentName);
  if (names.length && !names.includes(record.assessmentName)) {
    return false;
  }
  const referenceTypes = asList(query.referenceObjectType);
  if (
    referenceTypes.length &&
    !referenceTypes.includes(record.referenceObject.objectType)
  ) {
    return false;
  }
  if (
    query.referenceObjectId &&
    record.referenceObject.id !== query.referenceObjectId
  ) {
    return false;
  }
  if (
    query.referenceObjectVersion &&
    (record.referenceObject.version ?? "") !== query.referenceObjectVersion
  ) {
    return false;
  }
  const impactedValues = asBooleanList(query.impacted);
  if (impactedValues.length && !impactedValues.includes(record.impacted)) {
    return false;
  }
  const impactedTypes = asList(query.impactedObjectType);
  if (
    impactedTypes.length &&
    !impactedTypes.includes(record.impactedObject.objectType)
  ) {
    return false;
  }
  if (
    query.impactedObjectId &&
    record.impactedObject.id !== query.impactedObjectId
  ) {
    return false;
  }
  if (query.keyword?.trim()) {
    const kw = query.keyword.trim().toLowerCase();
    const haystack = [
      record.assessmentName,
      record.impactedObject.name,
      record.impactedObject.id,
      record.referenceObject.name,
      record.id,
    ]
      .join(" ")
      .toLowerCase();
    if (!haystack.includes(kw)) return false;
  }
  if (query.assessedFrom) {
    if (record.assessedAt < query.assessedFrom) return false;
  }
  if (query.assessedTo) {
    if (record.assessedAt > query.assessedTo) return false;
  }
  return true;
}

export async function mockFetchImplementationRecords(
  query: ImplementationRecordListQuery = {},
): Promise<ImplementationPaginatedResult<ImplementationRecord>> {
  await Promise.resolve();
  const page = query.page ?? 1;
  const pageSize = query.pageSize ?? 10;
  const filtered = MOCK_IMPLEMENTATION_RECORDS.filter((item) =>
    matchesQuery(item, query),
  );
  const start = (page - 1) * pageSize;
  return {
    items: filtered.slice(start, start + pageSize).map((item) => ({ ...item })),
    total: filtered.length,
    page,
    pageSize,
  };
}

export async function mockFetchAssessmentPackDetail(query: {
  referenceObjectType: string;
  referenceObjectId: string;
  referenceObjectVersion?: string;
}): Promise<AssessmentPack> {
  await Promise.resolve();
  const matched = MOCK_IMPLEMENTATION_RECORDS.filter(
    (item) =>
      item.referenceObject.objectType === query.referenceObjectType &&
      item.referenceObject.id === query.referenceObjectId &&
      (!query.referenceObjectVersion ||
        (item.referenceObject.version ?? "") === query.referenceObjectVersion),
  );
  if (matched.length === 0) {
    throw new Error("Assessment pack not found.");
  }
  const referenceObject = matched[0].referenceObject;
  const typeMap = new Map<string, { objectType: string; objectSubType: string; displayName: string }>();
  for (const item of matched) {
    const key = `${item.impactedObject.objectType}::${item.impactedObject.objectSubType}`;
    if (!typeMap.has(key)) {
      typeMap.set(key, {
        objectType: item.impactedObject.objectType,
        objectSubType: item.impactedObject.objectSubType,
        displayName:
          item.impactedObject.objectTypeName.trim() ||
          item.impactedObject.objectType,
      });
    }
  }
  return {
    name: "SoP Documents Assessment Pack",
    referenceObject: { ...referenceObject },
    impactedObjectTypes: [...typeMap.values()],
    // Placeholder labels from the pack banner design. Drop or replace once the API defines UGs.
    impactedUserGroups: ["PVRI", "NSC-1", "GPO", "PVRL-1", "PV Cluster"],
  };
}

export async function mockFetchImplementationRecordGroups(query: {
  referenceObjectType: string;
  referenceObjectId: string;
  referenceObjectVersion?: string;
  keyword?: string;
}): Promise<ImplementationRecordGroup[]> {
  await Promise.resolve();
  const matched = MOCK_IMPLEMENTATION_RECORDS.filter((item) =>
    matchesQuery(item, {
      referenceObjectType: query.referenceObjectType,
      referenceObjectId: query.referenceObjectId,
      referenceObjectVersion: query.referenceObjectVersion,
      keyword: query.keyword,
    }),
  );
  const groups = new Map<string, ImplementationRecordGroup>();
  for (const item of matched) {
    const existing = groups.get(item.assessmentName);
    if (existing) {
      existing.recordCount += 1;
      continue;
    }
    groups.set(item.assessmentName, {
      assessmentName: item.assessmentName,
      assessmentSource: item.assessmentSource,
      recordCount: 1,
    });
  }
  return [...groups.values()].sort((a, b) =>
    a.assessmentName.localeCompare(b.assessmentName),
  );
}
